import { useEffect, useState } from 'react';
import Wordmark from '../components/Wordmark.jsx';
import FilterBar from '../components/proofing/FilterBar.jsx';
import FinalDownloadsPanel from '../components/proofing/FinalDownloadsPanel.jsx';
import ProofImageGrid from '../components/proofing/ProofImageGrid.jsx';
import ProofLightbox from '../components/proofing/ProofLightbox.jsx';
import {
  fetchClientFinals,
  fetchClientGallery,
  fetchClientImages,
  loginClientGallery,
  updateClientRating
} from '../utils/proofingApi.js';

function getSlugFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[1] || '';
}

function countImages(images) {
  return images.reduce(
    (counts, image) => {
      counts.all += 1;
      counts[image.rating || 'unrated'] += 1;
      return counts;
    },
    { all: 0, unrated: 0, red: 0, yellow: 0, green: 0, final: 0 }
  );
}

export default function ClientGallery() {
  const slug = getSlugFromPath();
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [finals, setFinals] = useState(null);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('proofs');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [status, setStatus] = useState('Loading private gallery');

  async function loadGallery() {
    const [galleryPayload, nextImages, nextFinals] = await Promise.all([
      fetchClientGallery(slug),
      fetchClientImages(slug),
      fetchClientFinals(slug)
    ]);
    setGallery(galleryPayload.gallery);
    setImages(nextImages);
    setFinals(nextFinals);
    setAuthenticated(true);
    setStatus('');
  }

  useEffect(() => {
    loadGallery().catch(() => {
      setAuthenticated(false);
      setStatus('');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function handleLogin(event) {
    event.preventDefault();
    try {
      await loginClientGallery(slug, password);
      await loadGallery();
    } catch (error) {
      setStatus(error.message);
    }
  }

  async function handleRate(image, rating) {
    const previousImages = images;
    const nextImages = images.map((item) => (item.id === image.id ? { ...item, rating } : item));
    setImages(nextImages);
    setLightboxImage((current) => (current?.id === image.id ? { ...current, rating } : current));

    try {
      const result = await updateClientRating(slug, image.id, rating);
      setImages((current) => current.map((item) => (item.id === image.id ? result.image : item)));
      setFinals(await fetchClientFinals(slug));
    } catch (error) {
      setStatus(error.message);
      setImages(previousImages);
    }
  }

  if (!authenticated) {
    return (
      <main className="client-login">
        <section>
          <Wordmark />
          <p className="mono-label">Private proofing room</p>
          <h1>Enter the room</h1>
          <p>Diese Galerie ist geschützt. Gib das Shooting-Passwort ein, um Bilder zu sichten und final auszuwählen.</p>
          <form onSubmit={handleLogin}>
            <label>
              Galerie Passwort
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoFocus
              />
            </label>
            <button type="submit">Galerie öffnen</button>
          </form>
          {status && <p className="admin-status">{status}</p>}
          <a href="/">Stefan Maldet</a>
        </section>
      </main>
    );
  }

  const counts = countImages(images);
  const filteredImages = images.filter((image) => {
    if (filter === 'all') return true;
    if (filter === 'unrated') return !image.rating;
    return image.rating === filter;
  });

  return (
    <main className="client-gallery">
      <header className="client-gallery__header">
        <Wordmark compact />
        <nav>
          <button type="button" className={activeTab === 'proofs' ? 'is-active' : ''} onClick={() => setActiveTab('proofs')}>
            Auswahl
          </button>
          <button type="button" className={activeTab === 'downloads' ? 'is-active' : ''} onClick={() => setActiveTab('downloads')}>
            Download
          </button>
        </nav>
      </header>

      <section className="client-hero">
        <p className="mono-label">{gallery?.modelName || 'Private gallery'}</p>
        <h1>{gallery?.title}</h1>
        <p>{gallery?.description || 'Wähle deine Favoriten schnell und klar. Änderungen werden sofort gespeichert.'}</p>
      </section>

      {activeTab === 'proofs' ? (
        <>
          <section className="proof-instructions">
            <div>
              <p className="mono-label">Fast proofing</p>
              <h2>Rot raus. Gelb vielleicht. Grün stark. Final gewünscht.</h2>
            </div>
            <p>In der Lightbox funktionieren 1, 2, 3, 4 für Status und Pfeiltasten zum Wechseln.</p>
          </section>
          <FilterBar value={filter} onChange={setFilter} counts={counts} />
          <ProofImageGrid images={filteredImages} onOpen={setLightboxImage} onRate={handleRate} />
        </>
      ) : (
        <FinalDownloadsPanel slug={slug} finals={finals} />
      )}

      {status && <p className="floating-status">{status}</p>}
      <ProofLightbox
        image={lightboxImage}
        images={filteredImages}
        onClose={() => setLightboxImage(null)}
        onSelect={setLightboxImage}
        onRate={handleRate}
      />
    </main>
  );
}
